<template>
    <div
      v-if="configLoaded"
      class="app"
      :class="{
        'app--bordered': $config.border,
        'touch-enabled': isTouch
      }"
      :style="{
            '--accent-color': $config.accentColor,
            '--accent-color-text': accentColorTextColor,
            '--gradient-color-1': $config.gradient.color1,
            '--gradient-color-2': $config.gradient.color2,
        }">
        <svg style="visibility: hidden; position: absolute;">
            <defs>
                <linearGradient id="LinearGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0"
                          :style="{stopColor: $config.gradient.color1}" style="stop-opacity:1"/>
                    <stop offset="1"
                          :style="{stopColor: $config.gradient.color2}" style="stop-opacity:1"/>
                </linearGradient>
            </defs>
        </svg>
        <Offline/>
        <transition name="fade" appear>
            <div key="app" v-if="!loading" id="wrapper">
              <Slideshow v-if="slideshow" />
              <template v-else>
                <aside>
                  <img class="logo" :src="$config.logo"
                       alt="">
                  <px-nav class="main-nav"></px-nav>
                </aside>
                <main>
                  <transition name="fade">
                    <router-view class="main"></router-view>
                  </transition>
                </main>
              </template>
            </div>
            <div key="loader" class="loader" v-else>
                <lottie
                  :options="animation.defaultOptions"
                  :height="animation.size"
                  :width="animation.size"/>
            </div>
        </transition>
        <Lightbox />
    </div>
    <transition name="fade" appear v-else-if="configLoaded === false" >
        <install-dialog />
    </transition>
</template>

<script type="text/babel">
/* eslint-disable import/no-extraneous-dependencies */
import moment from 'moment';
import animationData from './assets/animations/loading.json';
import Lottie from './components/helpers/lottie.vue';
import navigation from './components/navigation.vue';
import Offline from './components/Offline.vue';
import InstallDialog from './components/InstallDialog.vue';
import Lightbox from './extensions/Lightbox.vue';
import Slideshow from './components/Slideshow.vue';

export default {
  name: 'pixel-touch',
  components: {
    'px-nav': navigation,
    lottie: Lottie,
    Offline,
    InstallDialog,
    Lightbox,
    Slideshow,
  },
  methods: {
    checkIfTouch() {
      if (!('ontouchstart' in window)) {
        this.slideshow = true;
      }
    },
    getContrastYIQ(color) {
      let hexcolor = color;
      if (hexcolor.length === 4) {
        hexcolor = `#${hexcolor.substr(1).split('').map((char) => char + char).join('')}`;
      }
      const r = parseInt(hexcolor.substr(1, 2), 16);
      const g = parseInt(hexcolor.substr(3, 2), 16);
      const b = parseInt(hexcolor.substr(5, 2), 16);
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return (yiq >= 165) ? 'black' : 'white';
    },
    async updateData() {
      await this.$store.dispatch('getConfig');
      this.configLoaded = true;

      await Promise.all([
        this.$store.dispatch('getAllMachines'),
        this.$store.dispatch('getAllArticles'),
        this.$store.dispatch('getAllSliders'),
        this.$store.dispatch('getAllEvents'),
        this.$store.dispatch('getAllGalleries'),
      ]);
      this.lastUpdate = moment();
    },
    async initialize() {
      try {
        const updateFunction = async () => {
          const lastUpdateDiff = moment().diff(this.lastUpdate);
          if (!this.lastUpdate || lastUpdateDiff > (this.$config.updateInterval * 60 * 1000)) {
            await this.updateData();
          }

          setTimeout(updateFunction, 5000);
        };
        await updateFunction();

        this.loading = false;
      } catch (err) {
        this.configLoaded = false;

        await setTimeout(this.initialize, 5000);
      }
    },
  },
  computed: {
    accentColorTextColor() {
      return this.getContrastYIQ(this.accentColor);
    },
  },
  data() {
    return {
      accentColor: '#8BC63E',
      gradient: {
        color1: '#8BC63E',
        color2: '#bcc64e',
      },
      loading: true,
      configLoaded: null,
      lastUpdate: null,
      isTouch: false,
      slideshow: false,
      animation: {
        size: 200,
        defaultOptions: {
          animationData,
        },
      },
    };
  },
  async created() {
    this.checkIfTouch();

    await this.initialize();
  },
};
</script>

<style lang="scss">
    :root {
        --main-bg-color: brown;
        --accent-color: #57af26;
        --navigation-bg: #fff;
    }

    .touch-enabled {
        cursor: none!important;
        a, button, input, video {
            cursor: none!important;
        }
    }

    .scroll-performance {
      will-change: transform;
    }

    .app-wrapper {
        display: flex;
    }

    html, body {
        font-family: Roboto, sans-serif;

        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }

    *, *:before, *:after {
        box-sizing: inherit;
    }

    .app {
        height: 100vh;
        width: 100vw;
        &--bordered {
            height: calc(100vh - 2em);
            width: calc(100vw - 2em);
            margin: 1em;
            box-shadow: 0 2px 25px rgba(0, 0, 0, 0.2), 0 5px 15px rgba(0, 0, 0, 0.1);
        }
        overflow: hidden;
        position: relative;
        background: #fff;
    }
    #wrapper {
        display: grid;
        width: 100%;
        height: 100%;
        grid-auto-columns: min-content 1fr;
        grid-template-areas: "aside main";
        grid-auto-rows: 100%;
        position: absolute;
    }
    .loader {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
    }
    aside {
        display: flex;
        flex-direction: column;
        background: var(--navigation-bg);
        padding: 1.5em;
        grid-area: aside;
        .logo {
            width: 100%;
            max-height: 7.5em;
            object-fit: contain;
        }
    }
    main, div.main {
        grid-area: main;
        overflow: hidden;
        position: relative;
        height: 100%;
        background: #fafafa;
    }

    .spacer {
        flex-grow: 100;
    }

    .page-enter-active, .page-leave-active {
        transition: opacity 1s, transform 1s;
        img {
            transition: filter .75s;
            transition-delay: .75s;
        }
    }
    .page-enter {
        transform: translateX(100%);
        img {
            filter: saturate(0.2) brightness(1.25) opacity(0);
        }
    }
    .page-leave-to {
        transform: translateX(-100%);
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity .5s;
    }
    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
        opacity: 0;
    }
</style>
